import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import { tagged } from "@mirage/service-logging";
import { getChatCompletionRequest } from "@mirage/shared/stack-item-summary/llmConfigs";

import { getCurrentAccount } from "@mirage/service-auth";
import { APIv2Callable } from "@mirage/service-dbx-api/service";
import { createAPIv2GRPCWebPromiseClient } from "@mirage/service-dbx-api/service/grpc/api_v2";
import { MLCapabilitiesStreamingApiV2 } from "@mirage/service-dbx-api/service/grpc/ml_capabilities_apiv2/gen/api_v2_service_streaming_connectweb";
import {
  ChatContent,
  ChatMessage,
  SystemMessage,
  UserMessage,
} from "@mirage/service-dbx-api/service/grpc/ml_capabilities_apiv2/gen/common_pb";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import { firstValueFrom, Observable } from "rxjs";
import { createPromptRecord, RecentPromptsCache } from "./cache";
import {
  backendPrompt,
  categoryRelevancePrompt,
  fileRelevancePrompt,
  imageRelevancePrompt,
  messageRelevancePrompt,
  rubricToSubject,
} from "./prompts";
import type {
  MemoryCache,
  PreviousPrompt,
  SearchRelevanceArgs,
  Service,
} from "./types";
import { PROMPT_TYPE, RelevanceRubric } from "./types";

const logger = tagged("compute-relevance");

interface DbxApiServiceContract {
  callApiV2: APIv2Callable;
}

export interface TypeaheadSearchServiceContract {
  getRecentQueries(): Observable<PreviousQuery[]>;
}

const rubricToPrompt: { [key in RelevanceRubric]: string } = {
  [RelevanceRubric.File]: fileRelevancePrompt,
  [RelevanceRubric.Message]: messageRelevancePrompt,
  [RelevanceRubric.Category]: categoryRelevancePrompt,
  [RelevanceRubric.Image]: imageRelevancePrompt,
};

/**
 * Creates appropriate ChatContent based on rubric type and available data
 */
export const createUserMessageContent = (
  rubric: RelevanceRubric,
  messageText: string,
  base64Image?: string,
): ChatContent => {
  switch (rubric) {
    case RelevanceRubric.Image:
      if (base64Image) {
        return new ChatContent({
          text: "",
          contentDicts: [
            // Text content as JSON string
            JSON.stringify({
              type: "text",
              text: messageText,
            }),
            // Base64 image content as JSON string
            JSON.stringify({
              type: "image_url",
              image_url: {
                url: base64Image,
              },
            }),
          ],
        });
      }

      // If no base64Image, treat as regular text content
      return new ChatContent({
        text: messageText,
      });

    default:
      return new ChatContent({
        text: messageText,
      });
  }
};

export type ComputeRelevanceService = Awaited<
  ReturnType<typeof computeRelevanceService>
>;

export default function computeRelevanceService(
  { callApiV2 }: DbxApiServiceContract,
  cache?: MemoryCache<PreviousPrompt>,
) {
  // Initialize the RecentPromptsCache - use provided cache or default to RecentPromptsCache
  const recentPromptsCache = cache || new RecentPromptsCache();

  return services.provide<Service>(
    ServiceId.COMPUTE_RELEVANCE,

    {
      computeRelevance: ({
        item,
        base64Image,
        query,
        activeFilters,
        prompt,
        rubric,
      }: SearchRelevanceArgs): Observable<string> => {
        // Get a stream to the file
        if (!item.url) {
          throw new Error("Item URL is required to fetch metadata");
        }

        // Ensure url is a string (TypeScript needs this reassurance)
        const itemUrl: string = item.url;

        // Get services once outside the stream
        const typeaheadSearchService =
          services.get<TypeaheadSearchServiceContract>(
            ServiceId.TYPEAHEAD_SEARCH,
          );

        // Cache the prompt for future reference
        let cachePromptId: string | null = null;
        if (prompt) {
          const promptRecord = createPromptRecord(prompt);
          cachePromptId = promptRecord.uuid;
          recentPromptsCache.addItem(promptRecord);
        }

        return new Observable<string>((subscriber) => {
          // Use a simple Promise chain to simplify the logic and avoid Observable type issues
          (async () => {
            try {
              // Use Promise.all to necessary prompt user content in parallel
              const [recentQueries, fileContent, accountDetails] =
                // Recent queries for personalization
                await Promise.all([
                  (async () => {
                    try {
                      const recentQueriesObservable =
                        typeaheadSearchService.getRecentQueries();
                      const recentQueries = await firstValueFrom(
                        recentQueriesObservable,
                      );

                      if (recentQueries && recentQueries.length > 0) {
                        // Take the 5 most recent queries
                        return recentQueries
                          .slice(0, 10)
                          .map((q: PreviousQuery) => q.query)
                          .filter((q: string) => q.length > 0 && q !== query);
                      }
                    } catch (error) {
                      logger.error("Error fetching recent queries", error);
                    }
                    return [];
                  })(),
                  // Get file content
                  (async () => {
                    // Skip file content fetching for images when we have base64Image
                    if (rubric === RelevanceRubric.Image && base64Image) {
                      return null;
                    }
                    return callApiV2("dcsGetLinkMetadata", {
                      url_or_uuids: [
                        {
                          link_type: {
                            ".tag": "url",
                            url: itemUrl,
                          },
                        },
                      ],
                      include_body: true,
                    });
                  })(),
                  // Get user account details for personalization
                  (async () => {
                    const currentAccount = await getCurrentAccount();
                    return {
                      accountId: currentAccount?.account_id,
                      accountType: currentAccount?.account_type,
                      email: currentAccount?.email,
                      uid: currentAccount?.uid,
                      name: currentAccount?.name,
                      team: currentAccount?.team,
                      teamMemberId: currentAccount?.team_member_id,
                      userName: currentAccount?.user_name,
                    };
                  })(),
                ]);

              // Set up the LLM request
              const model = "gpt-4o";
              const modelSource = "openai";
              const feature = "dash/compute-relevance";

              // Build userMessageText conditionally based on rubric type
              const baseMessageParts = [
                `Search Query: ${query}`,
                `Search Filters: ${JSON.stringify(activeFilters)}`,
                `${rubricToSubject[rubric]}: ${JSON.stringify(item)}`,
                `Search History: ${recentQueries?.length ? JSON.stringify(recentQueries) : "none"}`,
                `Account Details: ${JSON.stringify(accountDetails)}`,
              ];

              // Only include file content for non-image rubrics. For images we pass the base64Image directly.
              if (rubric !== RelevanceRubric.Image) {
                baseMessageParts.push(
                  `${rubricToSubject[rubric]} Content: ${JSON.stringify(fileContent)}`,
                );
              }

              const userMessageText = baseMessageParts.join("\n");

              if (!cachePromptId) {
                cachePromptId =
                  await recentPromptsCache.getDefaultPromptId(rubric);
              }
              const utilizedPrompt =
                (cachePromptId
                  ? recentPromptsCache
                      .all()
                      .find((p) => p.uuid === cachePromptId)?.prompt
                  : rubricToPrompt[rubric]) || rubricToPrompt[rubric];

              // Create user message content based on rubric type
              const userMessageContent = createUserMessageContent(
                rubric,
                userMessageText,
                base64Image,
              );

              const messages: ChatMessage[] = [
                new ChatMessage({
                  messageType: {
                    case: "systemMessage",
                    value: new SystemMessage({
                      content: new ChatContent({
                        text: utilizedPrompt,
                      }),
                    }),
                  },
                }),
                new ChatMessage({
                  messageType: {
                    case: "userMessage",
                    value: new UserMessage({
                      content: userMessageContent,
                    }),
                  },
                }),
              ];
              const request = getChatCompletionRequest(
                model,
                modelSource,
                feature,
                messages,
              );

              // Make the LLM call
              const mlcClient = createAPIv2GRPCWebPromiseClient(
                MLCapabilitiesStreamingApiV2,
              );

              let bufferedClosingBrace: string | null = null;
              let isFirstChunk = true;

              /**
               * This is one of the trickiest parts of the search relevance feature.
               * We get a streaming response from the LLM call, but want to send additional details around the request
               * back to the client. To do this, we buffer the last closing brace of the streaming JSON response,
               * then add our additional fields to the response. These fields being the prompt used, the user message content
               * which includes the file content and personalization details like search history and account details.
               */
              for await (const res of mlcClient.createChatCompletionStreaming(
                request,
              )) {
                const contentValue =
                  res.results[0]?.generations[0].chatMessage?.messageType.value
                    ?.content?.text;

                if (contentValue) {
                  // If we have a buffered closing brace, send it first
                  if (bufferedClosingBrace !== null) {
                    subscriber.next(bufferedClosingBrace);
                    bufferedClosingBrace = null;
                  }

                  // Check if this chunk ends with a closing brace
                  if (contentValue.endsWith("}")) {
                    // Split at the last closing brace
                    const lastBraceIndex = contentValue.lastIndexOf("}");
                    const contentWithoutBrace = contentValue.slice(
                      0,
                      lastBraceIndex,
                    );
                    bufferedClosingBrace = "}";

                    // Send the content without the closing brace
                    if (contentWithoutBrace) {
                      subscriber.next(contentWithoutBrace);
                    }
                  } else {
                    // If it doesn't end with a brace, just send the content
                    subscriber.next(contentValue);
                  }
                  isFirstChunk = false;
                }
              }

              // If we have a buffered closing brace at the end, add our extra fields
              if (bufferedClosingBrace !== null) {
                const extraFields: [string, string][] = [
                  ["sentPrompt", utilizedPrompt],
                  ["userMessageContent", userMessageText],
                ];
                if (cachePromptId) {
                  extraFields.push(["promptId", cachePromptId]);
                }

                // Add a comma if we're not at the start of the object
                if (!isFirstChunk) {
                  subscriber.next(",");
                }

                // Output extra fields, adding a comma if we're not at the start of the object
                for (let i = 0; i < extraFields.length; i++) {
                  if (i > 0) {
                    subscriber.next(",");
                  }
                  subscriber.next(
                    `"${extraFields[i][0]}": ${JSON.stringify(extraFields[i][1])}`,
                  );
                }

                // Send the final closing brace
                subscriber.next("}");
              }

              subscriber.complete();
            } catch (error) {
              logger.error("Error in relevance computation", error);
              subscriber.error(error);
            }
          })();
        });
      },

      fetchRecentPrompts: async (
        rubric: RelevanceRubric,
      ): Promise<(PreviousPrompt & { type: PROMPT_TYPE })[]> => {
        try {
          // Get all custom prompts
          const previousPrompts = recentPromptsCache.all();

          // Get the stored default prompt ID for this rubric
          const storedDefaultPromptId =
            await recentPromptsCache.getDefaultPromptId(rubric);

          // Create a frontend prompt record for the specified rubric
          const frontendPrompt = createPromptRecord(
            rubricToPrompt[rubric],
            PROMPT_TYPE.FRONTEND,
          );

          // Create a backend prompt record
          const backendPromptRecord = createPromptRecord(
            backendPrompt,
            PROMPT_TYPE.BACKEND,
          );

          // Generate set of normalized prompt texts for deduplication
          const promptTexts = new Set<string>();
          promptTexts.add(frontendPrompt.prompt.toLowerCase());
          promptTexts.add(backendPromptRecord.prompt.toLowerCase());

          // Filter out duplicates based on normalized text
          const filteredPrompts = previousPrompts.filter((prompt) => {
            const normalized = prompt.prompt.toLowerCase();
            // Skip if it's the same as frontend or backend prompt
            if (promptTexts.has(normalized)) {
              return false;
            }
            promptTexts.add(normalized);
            return true;
          });

          // Find the user-selected default prompt if it exists and is a custom prompt
          let userSelectedDefaultPrompt = null;
          let remainingCustomPrompts = filteredPrompts;

          if (storedDefaultPromptId) {
            const userDefault = filteredPrompts.find(
              (p) => p.uuid === storedDefaultPromptId,
            );
            if (userDefault) {
              userSelectedDefaultPrompt = userDefault;
              remainingCustomPrompts = filteredPrompts.filter(
                (p) => p.uuid !== storedDefaultPromptId,
              );
            }
          }

          // Build the prompts in the desired order: backend, frontend, user default (if custom), other custom
          const combinedPrompts = [
            { ...backendPromptRecord, type: PROMPT_TYPE.BACKEND },
            { ...frontendPrompt, type: PROMPT_TYPE.FRONTEND },
            ...(userSelectedDefaultPrompt
              ? [{ ...userSelectedDefaultPrompt, type: PROMPT_TYPE.CUSTOM }]
              : []),
            ...remainingCustomPrompts.map((prompt) => ({
              ...prompt,
              type: prompt.type || PROMPT_TYPE.CUSTOM,
            })),
          ];

          return combinedPrompts;
        } catch (error) {
          logger.error("Error fetching recent prompts", error);
          throw error;
        }
      },

      getDefaultPromptId: (rubric: RelevanceRubric): Promise<string | null> => {
        return recentPromptsCache.getDefaultPromptId(rubric);
      },

      setDefaultPromptId: async (
        rubric: RelevanceRubric,
        promptId: string | null,
      ): Promise<void> => {
        await recentPromptsCache.setDefaultPromptId(rubric, promptId);
      },

      deletePrompt: async (
        uuid: string,
        rubric: RelevanceRubric,
      ): Promise<(PreviousPrompt & { type: PROMPT_TYPE })[]> => {
        try {
          // Find the prompt to delete
          const promptToDelete = recentPromptsCache
            .all()
            .find((prompt) => prompt.uuid === uuid);

          if (!promptToDelete) {
            throw new Error(`Prompt with UUID ${uuid} not found`);
          }

          // Remove the prompt from the cache
          const updatedPrompts =
            await recentPromptsCache.removeItem(promptToDelete);

          logger.info("Prompt deleted successfully", { uuid });

          // Get the stored default prompt ID for this rubric
          const storedDefaultPromptId =
            await recentPromptsCache.getDefaultPromptId(rubric);

          // Create a frontend prompt record for the specified rubric
          const frontendPrompt = createPromptRecord(
            rubricToPrompt[rubric],
            PROMPT_TYPE.FRONTEND,
          );

          // Create a backend prompt record
          const backendPromptRecord = createPromptRecord(
            backendPrompt,
            PROMPT_TYPE.BACKEND,
          );

          // Generate set of normalized prompt texts for deduplication
          const promptTexts = new Set<string>();
          promptTexts.add(frontendPrompt.prompt.toLowerCase());
          promptTexts.add(backendPromptRecord.prompt.toLowerCase());

          // Filter out duplicates based on normalized text
          const filteredPrompts = updatedPrompts.filter((prompt) => {
            const normalized = prompt.prompt.toLowerCase();
            // Skip if it's the same as frontend or backend prompt
            if (promptTexts.has(normalized)) {
              return false;
            }
            promptTexts.add(normalized);
            return true;
          });

          // Find the user-selected default prompt if it exists and is a custom prompt
          let userSelectedDefaultPrompt = null;
          let remainingCustomPrompts = filteredPrompts;

          if (storedDefaultPromptId) {
            const userDefault = filteredPrompts.find(
              (p) => p.uuid === storedDefaultPromptId,
            );
            if (userDefault) {
              userSelectedDefaultPrompt = userDefault;
              remainingCustomPrompts = filteredPrompts.filter(
                (p) => p.uuid !== storedDefaultPromptId,
              );
            }
          }

          // Build the prompts in the desired order: backend, frontend, user default (if custom), other custom
          const combinedPrompts = [
            { ...backendPromptRecord, type: PROMPT_TYPE.BACKEND },
            { ...frontendPrompt, type: PROMPT_TYPE.FRONTEND },
            ...(userSelectedDefaultPrompt
              ? [{ ...userSelectedDefaultPrompt, type: PROMPT_TYPE.CUSTOM }]
              : []),
            ...remainingCustomPrompts.map((prompt) => ({
              ...prompt,
              type: prompt.type || PROMPT_TYPE.CUSTOM,
            })),
          ];

          return combinedPrompts;
        } catch (error) {
          logger.error("Error in deletePrompt", {
            errorMessage:
              error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
      },
    },
    [ServiceId.DBX_API, ServiceId.TYPEAHEAD_SEARCH],
  );
}
